import { BaseGameMode } from './BaseGameMode';
import { Graphics } from 'pixi.js';
import { cameraShake } from '../utils';
import { CANVAS_HEIGHT, setCanvasHeight } from '../constants';

export class snakeupMode extends BaseGameMode {
    
    private trailBodyCooldown: number = 0;
    private tailSegments: any[] = []; // Keep track of created tail segments
    private readonly MAX_TAIL_SEGMENTS = 50; // Max number of segments in the tail
    private readonly TAIL_START_DELAY = 350; // 150ms delay before tail starts
    private readonly SEGMENT_SPACING = 10; // Distance between segments
    private readonly TAIL_LIFETIME_MS = 3000; // 20s safety expiration to prevent trapping
    private lastSegmentPos: { x: number, y: number } | null = null;
    private positionBuffer: { x: number, y: number, time: number }[] = [];
    private passableSegments: Set<number> = new Set(); // Segments that can be passed through
    private readonly PASSTHROUGH_DURATION = 500; // How long a segment stays passable (ms)
    private readonly DELETE_RADIUS = 80; // Radius for right-click deletion of persistent platforms (centered on ball)
    private originalCanvasHeight: number = 5400; // Store original height to restore later
    private readonly STANDARD_HEIGHT = 5400; // Standard canvas height

    init(): void {
        super.init();
        // Double the canvas height for SnakeUp mode
        this.originalCanvasHeight = CANVAS_HEIGHT;
        setCanvasHeight(this.originalCanvasHeight * 2);
    }

    destroy(): void {
        super.destroy();
        // Restore original canvas height
        setCanvasHeight(this.originalCanvasHeight);
    }

    getLevelHeight(): number | undefined {
        // Return double the standard height for SnakeUp mode
        return this.STANDARD_HEIGHT * 2;
    }

    getModeName(): string {
        return "snakeup";
    }

    shouldGeneratePlatforms(): boolean {
        return false; // NO PLATFORMS! We make our own.
    }

    onBallStrike(position: { x: number, y: number }, power: number, angle: number): void {
        super.onBallStrike(position, power, angle);
        
        // Check if clicking on a tail segment for pass-through
        const strikePos = (this.ballManager as any).lastStrikePosition;
        if (strikePos) {
            this.checkAndMakePassable(strikePos, angle);
        }
    }

    private checkAndMakePassable(strikePos: { x: number, y: number }, strikeAngle: number): void {
        // Check each tail segment to see if the strike is on/near it
        for (const segment of this.tailSegments) {
            if (segment.permanent) continue; // Can't pass through permanent segments
            
            // Calculate distance from strike point to segment center
            const dx = strikePos.x - segment.graphics.x;
            const dy = strikePos.y - segment.graphics.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            // If strike is within segment bounds (roughly)
            if (distance < segment.length / 2 + 30) {
                // Check if strike direction aligns with segment direction
                const segmentAngle = segment.graphics.rotation;
                const angleDiff = Math.abs(strikeAngle - segmentAngle);
                const normalizedDiff = Math.min(angleDiff, Math.PI * 2 - angleDiff);
                
                // If angles are roughly aligned (within 45 degrees)
                if (normalizedDiff < Math.PI / 4 || normalizedDiff > Math.PI * 7 / 4) {
                    // Make this segment passable
                    this.passableSegments.add(segment.id);
                    
                    // Make it a sensor in physics (temporarily)
                    (this.ballManager as any).matterWorker.postMessage({
                        type: 'setBodySensor',
                        id: segment.id,
                        isSensor: true
                    });
                    
                    // Restore collision after duration
                    setTimeout(() => {
                        this.passableSegments.delete(segment.id);
                        (this.ballManager as any).matterWorker.postMessage({
                            type: 'setBodySensor',
                            id: segment.id,
                            isSensor: false
                        });
                    }, this.PASSTHROUGH_DURATION);
                    
                    break; // Only make one segment passable per strike
                }
            }
        }
    }

    update(dt: number): void {
        super.update(dt);
        
        // Constant Tail Creation Logic
        if (this.ballManager.inMotion) {
            const currentPos = this.ballManager.getPosition();
            if (currentPos) {
                // Add current position to history buffer with timestamp
                this.positionBuffer.push({
                    x: currentPos.x,
                    y: currentPos.y,
                    time: Date.now()
                });

                // Process buffer: find points old enough to be added to the tail
                const now = Date.now();
                
                // Process as many valid points as available to maintain continuity
                while (this.positionBuffer.length > 0) {
                    const candidate = this.positionBuffer[0]; // Peek at oldest
                    
                    if (now - candidate.time >= this.TAIL_START_DELAY) {
                        // This point is old enough to be part of the tail
                        
                        if (!this.lastSegmentPos) {
                            // Initialize last pos if we haven't started yet
                            this.lastSegmentPos = { x: candidate.x, y: candidate.y };
                        } else {
                            const dx = candidate.x - this.lastSegmentPos.x;
                            const dy = candidate.y - this.lastSegmentPos.y;
                            const distance = Math.sqrt(dx*dx + dy*dy);
                            
                            if (distance >= this.SEGMENT_SPACING) {
                                this.createTailSegment({ x: candidate.x, y: candidate.y });
                                this.lastSegmentPos = { x: candidate.x, y: candidate.y };
                            }
                        }
                        
                        // Remove processed point
                        this.positionBuffer.shift();
                    } else {
                        // Oldest point not old enough yet, stop processing
                        break;
                    }
                }
            }
        } else {
            // Clear buffer when ball stops to prevent delayed tail formation while stationary
            this.positionBuffer = [];
            this.lastSegmentPos = null;
        }

        // Safety expiry logic (in case player gets trapped)
        this.updateTailLifecycle(dt);

        // Visual Updates (Fading the tip)
        this.updateTailVisuals();
    }

    hasPersistentUpgrades(): boolean {
        return false;
    }

    hasInterstitialPowerups(): boolean {
        return false;
    }

    hasDoubleJump(): boolean {
        return false;
    }

    hasRounds(): boolean {
        return false;
    }

    hasTimer(): boolean {
        return false;
    }

    hasLevelUps(): boolean {
        return false;
    }

    private createTailSegment(currentPos: { x: number, y: number }): void {
        // If no last position, just set it and return (need two points for a segment)
        if (!this.lastSegmentPos) {
            this.lastSegmentPos = currentPos;
            return;
        }

        const segmentId = Date.now() + Math.random();
        
        const dx = currentPos.x - this.lastSegmentPos.x;
        const dy = currentPos.y - this.lastSegmentPos.y;
        const length = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);
        const midX = (this.lastSegmentPos.x + currentPos.x) / 2;
        const midY = (this.lastSegmentPos.y + currentPos.y) / 2;
        
        // Check if mouse is held down - if so, this segment is permanent
        const isPermanent = (this.ballManager as any).isMouseDown ? (this.ballManager as any).isMouseDown() : false;
        
        // Tell physics engine to create a static rectangle connecting the points
        (this.ballManager as any).matterWorker.postMessage({ 
            type: 'createStaticRectangle', 
            x: midX, 
            y: midY, 
            width: length,
            height: 10, // Thickness of the tail
            angle: angle,
            id: segmentId
        });
        
        // Create visual representation
        const graphics = new Graphics();
        // Draw a rotated rectangle visually
        graphics.rect(-length / 2, -5, length, 10); // centered at 0,0
        
        // Color: green for permanent, white for temporary (ephemeral)
        const color = isPermanent ? 0x00FF00 : 0xFFFFFF;
        graphics.fill({ color: color, alpha: 0.85 });
        graphics.x = midX;
        graphics.y = midY;
        graphics.rotation = angle;
        
        (this.ballManager as any).gameWorld.addChild(graphics);

        this.tailSegments.push({
            id: segmentId,
            graphics: graphics,
            age: 0,
            permanent: isPermanent,
            length: length
        });

        // Maintain Max Length - only count temporary segments
        // Permanent segments don't count towards the limit and persist indefinitely
        const temporarySegments = this.tailSegments.filter(s => !s.permanent);
        if (temporarySegments.length > this.MAX_TAIL_SEGMENTS) {
            // Remove oldest temporary segment
            for (let i = 0; i < this.tailSegments.length; i++) {
                if (!this.tailSegments[i].permanent) {
                    const removed = this.tailSegments.splice(i, 1)[0];
                    this.removeSegment(removed);
                    break;
                }
            }
        }
    }

    private updateTailLifecycle(dt: number): void {
        const dtMs = dt * 16.66; // Convert Pixi units to ms

        // Iterate backwards to safely splice
        for (let i = this.tailSegments.length - 1; i >= 0; i--) {
            const segment = this.tailSegments[i];
            
            // Skip permanent segments - they never expire
            if (segment.permanent) {
                continue;
            }
            
            segment.age += dtMs;

            if (segment.age >= this.TAIL_LIFETIME_MS) {
                 const removed = this.tailSegments.splice(i, 1)[0];
                 this.removeSegment(removed);
            }
        }
    }

    private removeSegment(segment: any): void {
        if (!segment) return;

        // Remove from visual world
        if (segment.graphics.parent) {
            segment.graphics.parent.removeChild(segment.graphics);
        }
        
        // Tell physics worker to remove the body
        (this.ballManager as any).matterWorker.postMessage({
            type: 'removeBody',
            id: segment.id
        });
    }

    private updateTailVisuals(): void {
        // Fade out the tip of the tail (oldest segments)
        const FADE_LENGTH = 15; // Number of segments to fade
        
        for (let i = 0; i < this.tailSegments.length; i++) {
            const segment = this.tailSegments[i];
            
            // Permanent segments stay at full alpha and green color
            if (segment.permanent) {
                segment.graphics.alpha = 0.85;
                // Ensure it stays green
                segment.graphics.clear();
                segment.graphics.rect(-segment.length / 2, -5, segment.length, 10);
                segment.graphics.fill({ color: 0x00FF00, alpha: 0.85 });
                continue;
            }
            
            // Default alpha for temporary segments (white)
            let alpha = 0.85;

            // If it's near the tip (index 0 is oldest)
            if (i < FADE_LENGTH) {
                alpha = (i / FADE_LENGTH) * 0.85;
            }
            
            segment.graphics.alpha = alpha;
        }
    }

    getPhysicsSettings(): any {
        // Snake mode might need lower gravity to make climbing easier
        return {
            gravity: { x: 0, y: 0.4 }, // Softer gravity (was 0.6, default 1.0)
            airFriction: 0.02
        };
    }

    getStrikeMultiplier(): number {
        return 0.6; // Much weaker jump
    }

    shouldShowPlayerLabels(): boolean {
        return false;
    }

    hasCombos(): boolean {
        return false;
    }

    shouldShowScorePopups(): boolean {
        return false;
    }

    onRightClick(worldX: number, worldY: number): void {
        // Use ball position instead of mouse position
        const ballPos = this.ballManager.getPosition();
        if (!ballPos) return;
        
        const segmentsToDelete: any[] = [];
        
        // Find all permanent segments within radius of ball
        for (let i = this.tailSegments.length - 1; i >= 0; i--) {
            const segment = this.tailSegments[i];
            if (!segment.permanent) continue; // Only delete permanent segments
            
            // Calculate distance from ball to segment center
            const segmentX = segment.graphics.x;
            const segmentY = segment.graphics.y;
            const dx = ballPos.x - segmentX;
            const dy = ballPos.y - segmentY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance <= this.DELETE_RADIUS) {
                segmentsToDelete.push(segment);
            }
        }
        
        if (segmentsToDelete.length === 0) return;
        
        // Play destruction sound
        const soundManager = (this.ballManager as any).soundManager;
        if (soundManager && soundManager.playSound) {
            soundManager.playSound('/sound/sfx/Bubble_Woosh_1.mp3', soundManager.sfxVolume || 0.5);
        }
        
        // Shake camera
        const app = (this.ballManager as any).app;
        if (app && app.stage) {
            cameraShake(app.stage, 6, 200);
        }
        
        // Create visual effects at ball position
        this.createDeletionEffect(ballPos.x, ballPos.y, segmentsToDelete.length);
        
        // Remove segments
        for (const segment of segmentsToDelete) {
            const index = this.tailSegments.indexOf(segment);
            if (index !== -1) {
                this.tailSegments.splice(index, 1);
                this.removeSegment(segment);
            }
        }
    }

    private createDeletionEffect(x: number, y: number, count: number): void {
        const gameWorld = (this.ballManager as any).gameWorld;
        const app = (this.ballManager as any).app;
        
        // Create green explosion flash
        const flash = new Graphics();
        flash.circle(0, 0, this.DELETE_RADIUS);
        flash.fill({ color: 0x00FF00, alpha: 0.6 });
        flash.x = x;
        flash.y = y;
        gameWorld.addChild(flash);
        
        // Animate flash
        let flashAlpha = 0.6;
        const flashTween = () => {
            flashAlpha -= 0.1;
            flash.alpha = flashAlpha;
            if (flashAlpha > 0) {
                requestAnimationFrame(flashTween);
            } else {
                gameWorld.removeChild(flash);
                flash.destroy();
            }
        };
        requestAnimationFrame(flashTween);
        
        // Create green particle burst
        const particleCount = Math.min(count * 3, 30);
        const greenColors = [0x00FF00, 0x00CC00, 0x00AA00, 0x88FF88, 0x44FF44]; // Various green shades
        for (let i = 0; i < particleCount; i++) {
            const particle = new Graphics();
            const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.5;
            const speed = 2 + Math.random() * 3;
            const size = 3 + Math.random() * 5;
            const color = greenColors[Math.floor(Math.random() * greenColors.length)];
            
            particle.circle(0, 0, size);
            particle.fill({ color: color, alpha: 0.9 });
            particle.x = x;
            particle.y = y;
            gameWorld.addChild(particle);
            
            // Animate particle
            let particleX = x;
            let particleY = y;
            let particleVx = Math.cos(angle) * speed;
            let particleVy = Math.sin(angle) * speed;
            let particleLife = 1.0;
            
            const particleTween = () => {
                particleX += particleVx;
                particleY += particleVy;
                particleVy += 0.2; // Gravity
                particleVx *= 0.98; // Friction
                particleVy *= 0.98;
                particleLife -= 0.02;
                
                particle.x = particleX;
                particle.y = particleY;
                particle.alpha = particleLife;
                particle.scale.set(particleLife);
                
                if (particleLife > 0) {
                    requestAnimationFrame(particleTween);
                } else {
                    gameWorld.removeChild(particle);
                    particle.destroy();
                }
            };
            requestAnimationFrame(particleTween);
        }
        
        // Create green confetti manually
        const confettiCount = 15;
        const greenConfettiColors = [0x00FF00, 0x00CC00, 0x00AA00, 0x88FF88, 0x44FF44, 0x00FF88];
        for (let i = 0; i < confettiCount; i++) {
            const confetti = new Graphics();
            const color = greenConfettiColors[Math.floor(Math.random() * greenConfettiColors.length)];
            confetti.rect(-4, -4, 8, 8);
            confetti.fill({ color: color, alpha: 1 });
            confetti.x = x;
            confetti.y = y;
            confetti.scale.set(0.5 + Math.random() * 0.5);
            gameWorld.addChild(confetti);
            
            const angle = Math.random() * Math.PI * 2;
            const speed = 2 + Math.random() * 4;
            let confettiX = x;
            let confettiY = y;
            let confettiVx = Math.cos(angle) * speed;
            let confettiVy = Math.sin(angle) * speed;
            let confettiLife = 1.0;
            let confettiRotation = 0;
            
            const confettiTween = () => {
                confettiX += confettiVx;
                confettiY += confettiVy;
                confettiVy += 0.1; // Gravity
                confettiVx *= 0.98;
                confettiVy *= 0.98;
                confettiLife -= 0.02;
                confettiRotation += 0.1;
                
                confetti.x = confettiX;
                confetti.y = confettiY;
                confetti.alpha = confettiLife;
                confetti.rotation = confettiRotation;
                
                if (confettiLife > 0) {
                    requestAnimationFrame(confettiTween);
                } else {
                    gameWorld.removeChild(confetti);
                    confetti.destroy();
                }
            };
            requestAnimationFrame(confettiTween);
        }
    }
}

